import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import {SVGIcon} from '@homeaway/react-svg';
import {CHEVRON_DOWN__12} from '@homeaway/svg-defs';
import './FormSelect.less';

function FormSelect({
    children,
    className,
    errorMsg,
    floatingLabel,
    id,
    itemLabelKey,
    itemValueKey,
    items,
    label,
    name,
    onChange,
    value,
    ...rest
}) {
    const formSelectClasses = classNames(
        'form-group',
        className,
        {
            'not-empty':
                !!value ||
                !!(items && items[0] && items[0][itemLabelKey]) ||
                !!children
        },
        {
            'has-error': !!errorMsg
        },
        {
            'floating-label': !!floatingLabel
        }
    );
    const options = items
        ? items.map((item, index) => {
              return (
                  <option
                      disabled={item.disabled}
                      hidden={item.hidden}
                      key={index}
                      value={item[itemValueKey]}
                  >
                      {item[itemLabelKey]}
                  </option>
              );
          })
        : null;

    return (
        <div className={formSelectClasses}>
            <label htmlFor={id}>{label}</label>
            <div className="FormSelect__wrapper">
                <select
                    id={id}
                    aria-label={label}
                    name={name}
                    className="form-control FormSelect__select"
                    value={value ? value : ''}
                    onChange={onChange}
                    {...rest}
                >
                    {children}
                    {options}
                </select>
                <SVGIcon
                    markup={CHEVRON_DOWN__12}
                    className="FormSelect__chevron"
                    aria-hidden
                />
            </div>
            {errorMsg && (
                <span
                    className="help-block"
                    dangerouslySetInnerHTML={{__html: errorMsg}}
                />
            )}
        </div>
    );
}

FormSelect.propTypes = {
    /** Error message to display, if needed. Will also turn on .has-error className */
    errorMsg: PropTypes.string,
    /** Flag to use default Baseline floating label tag. Setting to false will have the label display as a block element above the form select */
    floatingLabel: PropTypes.bool,
    /** id for label, required for a11y.  A generated one is provided if not specified, which may not play nicely with SSR.  It is best to provide a unique id yourself. */
    id: PropTypes.string,
    /** Key to use for option label in items array */
    itemLabelKey: PropTypes.string,
    /** Key to use for option value in items array */
    itemValueKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Array of items to feed to FormSelect as child <option> tags */
    items: PropTypes.array, // eslint-disable-line react/forbid-prop-types
    /** Form label for the select */
    label: PropTypes.string.isRequired,
    /** Name attribrute for the select tag */
    name: PropTypes.string.isRequired,
    /** Event handler to wire in when the FormSelect changes */
    onChange: PropTypes.func,
    /** The current selected value, if there is one */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

FormSelect.defaultProps = {
    floatingLabel: true,
    itemLabelKey: 'label',
    itemValueKey: 'value',
    id: `select-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
    onChange: () => {}
};

FormSelect.displayName = 'FormSelect';

export default FormSelect;
